import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaUsers,
  FaCheckCircle,
  FaSpinner,
  FaExclamationTriangle,
  FaCalendarAlt,
} from "react-icons/fa";
import PageHeader from "../../../core/common/header/PageHeader";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

type Batch = {
  id: number;
  name: string;
  topic: string;
};

type ExamSet = {
  id: number;
  examSetName: string;
  level: string;
  variant: string;
  totalDuration: number;
};

type AssignedBatchWithSchedule = {
  batchId: number;
  batchName: string;
  scheduledStartTime: string;
  scheduledEndTime: string;
};

const AdminAssignExam: React.FC = () => {
  const { examId } = useParams<{ examId: string }>();
  const navigate = useNavigate();

  const [exam, setExam] = useState<ExamSet | null>(null);
  const [allBatches, setAllBatches] = useState<Batch[]>([]);
  const [assignedBatchesWithSchedule, setAssignedBatchesWithSchedule] =
    useState<AssignedBatchWithSchedule[]>([]);
  const [selectedBatches, setSelectedBatches] = useState<number[]>([]);
  const [scheduledStartTime, setScheduledStartTime] = useState("");
  const [scheduledEndTime, setScheduledEndTime] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchData();
  }, [examId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch exam details
      const examResponse = await fetch(
        `${API_BASE_URL}/api/admin/exams/${examId}`,
        { credentials: "include" }
      );
      if (examResponse.ok) {
        const examResult = await examResponse.json();
        // Extract from SuccessResponse wrapper if needed
        const examData = examResult.data || examResult;
        setExam(examData);

        // Fetch active batches
        const batchesResponse = await fetch(
          `${API_BASE_URL}/api/admin/writing-tasks/active-batches`,
          { credentials: "include" }
        );
        if (batchesResponse.ok) {
          const batchesResult = await batchesResponse.json();
          // Extract from SuccessResponse wrapper if needed
          const batchesData = batchesResult.data || batchesResult;
          setAllBatches(batchesData);
        }

        // Fetch already assigned batches with schedules
        const assignmentsResponse = await fetch(
          `${API_BASE_URL}/api/admin/exams/${examId}/assigned-batches`,
          { credentials: "include" }
        );
        if (assignmentsResponse.ok) {
          const result = await assignmentsResponse.json();
          // Extract data from SuccessResponse wrapper
          const scheduleData = result.data || result;
          setAssignedBatchesWithSchedule(scheduleData);
        }
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Get assigned batch IDs for filtering
  const assignedBatchIds = assignedBatchesWithSchedule.map(
    (assigned) => assigned.batchId
  );

  // Filter batches by exam level AND exclude already assigned batches
  // ✅ FIX: Add null-safe checks for batch.topic and exam?.level
  const filteredBatches = allBatches.filter(
    (batch) =>
      batch.topic &&
      exam?.level &&
      batch.topic.toUpperCase() === exam.level.toUpperCase() &&
      !assignedBatchIds.includes(batch.id)
  );

  const handleBatchToggle = (batchId: number) => {
    setSelectedBatches((prev) =>
      prev.includes(batchId)
        ? prev.filter((id) => id !== batchId)
        : [...prev, batchId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (selectedBatches.length === 0) {
      setError("Please select at least one batch");
      return;
    }
    if (!scheduledStartTime) {
      setError("Please select a start time");
      return;
    }
    if (!scheduledEndTime) {
      setError("Please select an end time");
      return;
    }

    // Validate end time is after start time
    if (new Date(scheduledEndTime) <= new Date(scheduledStartTime)) {
      setError("End time must be after start time");
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/exams/assign`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          examSetId: parseInt(examId!),
          batchIds: selectedBatches,
          scheduledStartTime: scheduledStartTime,
          scheduledEndTime: scheduledEndTime,
        }),
      });

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          navigate("/admin/exam-management");
        }, 1500);
      } else {
        const data = await response.json();
        setError(
          data.error || "Failed to assign exam. It may already be assigned."
        );
      }
    } catch (err) {
      console.error("Error assigning exam:", err);
      setError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const formatSchedule = (startTime: string, endTime: string) => {
    const start = new Date(startTime).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
    const end = new Date(endTime).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
    return { start, end };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <FaSpinner className="animate-spin text-[#009fe3] text-4xl" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaUsers />}
        title="Assign Exam to Batches"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Exams", to: "/admin/exam-management" },
          { label: "Assign" },
        ]}
      />

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {success && (
          <div className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
            <FaCheckCircle className="text-green-600 text-xl" />
            <div>
              <h3 className="font-semibold text-green-800">Success!</h3>
              <p className="text-sm text-green-700">
                Exam assigned successfully. Redirecting...
              </p>
            </div>
          </div>
        )}

        {/* Exam Info Card */}
        {exam && (
          <div className="mb-6 bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-lg p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              Exam Details
            </h3>
            <div className="space-y-2">
              <div>
                <span className="font-semibold text-gray-700">Exam: </span>
                <span className="text-gray-900">{exam.examSetName}</span>
              </div>
              <div className="flex items-center gap-4">
                <div>
                  <span className="font-semibold text-gray-700">Level: </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold bg-blue-100 text-blue-800">
                    {exam.level}
                  </span>
                </div>
                <div>
                  <span className="font-semibold text-gray-700">Variant: </span>
                  <span className="text-gray-900">{exam.variant}</span>
                </div>
                <div>
                  <span className="font-semibold text-gray-700">
                    Duration:{" "}
                  </span>
                  <span className="text-gray-900">
                    {exam.totalDuration} minutes
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Already Assigned Batches */}
        {assignedBatchesWithSchedule.length > 0 && (
          <div className="mb-6 bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FaCheckCircle className="text-green-600" />
              Already Assigned Batches
            </h3>
            <div className="space-y-2">
              {assignedBatchesWithSchedule.map((assigned) => {
                const schedule = formatSchedule(
                  assigned.scheduledStartTime,
                  assigned.scheduledEndTime
                );
                return (
                  <div
                    key={assigned.batchId}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 bg-green-50 border border-green-200 rounded-lg gap-2"
                  >
                    <div className="flex items-center gap-2">
                      <FaCheckCircle className="text-green-600 text-sm flex-shrink-0" />
                      <span className="font-medium text-gray-900">
                        {assigned.batchName}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 ml-6 sm:ml-0">
                      <div className="flex items-center gap-2">
                        <FaCalendarAlt className="text-orange-500" />
                        <span>
                          <strong>Start:</strong> {schedule.start}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <FaCalendarAlt className="text-red-500" />
                        <span>
                          <strong>End:</strong> {schedule.end}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Assignment Form */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Select Batches */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Select Batches * (Level: {exam?.level})
              </label>
              {filteredBatches.length === 0 ? (
                <div className="text-center py-8 bg-amber-50 border border-amber-200 rounded-lg">
                  <FaExclamationTriangle className="mx-auto text-amber-500 text-3xl mb-2" />
                  <p className="text-amber-700 font-semibold">
                    No {exam?.level} level batches available
                  </p>
                  <p className="text-sm text-amber-600 mt-1">
                    {assignedBatchesWithSchedule.length > 0
                      ? `All ${exam?.level} batches have already been assigned this exam.`
                      : `Create a batch with topic "${exam?.level}" to assign this exam.`}
                  </p>
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3">
                  {filteredBatches.map((batch) => (
                    <label
                      key={batch.id}
                      className="flex items-center gap-3 p-3 rounded-lg transition-colors bg-gray-50 hover:bg-blue-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedBatches.includes(batch.id)}
                        onChange={() => handleBatchToggle(batch.id)}
                        className="w-5 h-5 text-[#009fe3] focus:ring-[#009fe3] rounded"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">
                          {batch.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          Topic: {batch.topic}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
              <div className="text-xs text-gray-500 mt-2">
                Selected: {selectedBatches.length} batch(es) • Already assigned:{" "}
                {assignedBatchesWithSchedule.length} batch(es)
              </div>
            </div>

            {/* Scheduled Start Time */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Scheduled Start Time *
              </label>
              <input
                type="datetime-local"
                value={scheduledStartTime}
                onChange={(e) => setScheduledStartTime(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent transition-all"
                style={{ fontSize: "16px" }}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                When students can start taking the exam
              </p>
            </div>

            {/* Scheduled End Time */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Scheduled End Time *
              </label>
              <input
                type="datetime-local"
                value={scheduledEndTime}
                onChange={(e) => setScheduledEndTime(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent transition-all"
                style={{ fontSize: "16px" }}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Last time students can submit the exam (must be after start
                time)
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={
                  submitting ||
                  success ||
                  filteredBatches.length === 0 ||
                  selectedBatches.length === 0
                }
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white rounded-lg font-semibold hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                {submitting ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    Assigning...
                  </>
                ) : (
                  <>
                    <FaUsers />
                    Assign to Batches
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => navigate("/admin/exam-management")}
                disabled={submitting}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default AdminAssignExam;
